/** Renders one schema-declared tag-data field with the right widget for its
 *  type + `x-` flags. Content flags compose: a field can be multiline + i18n +
 *  Django-template + HTML at once. The actual string editing is delegated to the
 *  shared TagValueInput (WYSIWYG↔raw for HTML), used both here and per language
 *  in the multilingual field. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { isHtml, isMultilingual, isTemplate, X_ENUM_LABELS, type SchemaProp } from '../schema/vocabulary';
import { JsonValueField } from './JsonValueField';
import { asLangMap, MultilingualField } from './MultilingualField';
import type { LeafBindings } from './translationBindings';
import { TagValueInput, type ValueFlags } from './TagValueInput';

interface Props {
  propKey: string;
  prop: SchemaProp;
  value: unknown;
  required?: boolean;
  readOnly?: boolean;
  onChange: (next: unknown) => void;
  /** Translation bindings of this attribute's language leaves (multilingual only, A6). */
  bindings?: Record<string, LeafBindings>;
}

function leafFlagsOf(prop: SchemaProp): ValueFlags {
  return { html: isHtml(prop), template: isTemplate(prop), multiline: prop.multiline === true };
}

function fieldChips(prop: SchemaProp) {
  const plainMultiline = prop.multiline === true && !isHtml(prop) && !isTemplate(prop);
  return (
    <>
      {isMultilingual(prop) && <Chip size="small" color="info" label="i18n" />}
      {isTemplate(prop) && <Chip size="small" variant="outlined" label="template" />}
      {isHtml(prop) && <Chip size="small" variant="outlined" label="HTML" />}
      {plainMultiline && <Chip size="small" variant="outlined" label="multiline" />}
    </>
  );
}

function FieldHeader({ label, required, prop }: { label: string; required?: boolean; prop: SchemaProp }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {label}{required ? ' *' : ''}
      </Typography>
      {fieldChips(prop)}
    </Stack>
  );
}

function stripTags(s: string): string {
  return s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function multilingualSummary(value: unknown): string {
  const map = asLangMap(value);
  const langs = Object.keys(map);
  const first = langs[0];
  if (first === undefined) return 'No languages';
  const rest = langs.slice(1);
  return `${stripTags(map[first] ?? '') || '(empty)'} (${first}${rest.length ? ` + ${rest.join(', ')}` : ''})`;
}

export function TagDataField({ propKey, prop, value, required, readOnly = false, onChange, bindings }: Props) {
  const label = prop.title || propKey;
  const type = prop.type ?? 'string';
  const flags = leafFlagsOf(prop);
  const help = prop.description ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
      {prop.description}
    </Typography>
  ) : null;

  // Multilingual — collapsible; summary shows the first language + other codes.
  if (isMultilingual(prop)) {
    return (
      <Accordion
        disableGutters defaultExpanded={false}
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}{required ? ' *' : ''}</Typography>
            {fieldChips(prop)}
            {bindings && Object.values(bindings).some((leaf) => leaf.asTarget) && (
              <Chip size="small" variant="outlined" color="info" label="auto-translated" />
            )}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {multilingualSummary(value)}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <MultilingualField
            value={value}
            readOnly={readOnly}
            leafFlags={flags}
            bindings={bindings}
            onChange={(next) => onChange(Object.keys(next).length ? next : undefined)}
          />
          {help}
        </AccordionDetails>
      </Accordion>
    );
  }

  // Object / array — pretty-print, click to open the JSON editor.
  if (type === 'object' || type === 'array') {
    return (
      <Box>
        <FieldHeader label={label} required={required} prop={prop} />
        <JsonValueField value={value} readOnly={readOnly} onChange={(next) => onChange(next)} />
        {help}
      </Box>
    );
  }

  // Enum → single-select.
  if (Array.isArray(prop.enum) && prop.enum.length) {
    const labels = prop[X_ENUM_LABELS];
    return (
      <Box>
        <FieldHeader label={label} required={required} prop={prop} />
        <TextField
          select size="small" fullWidth disabled={readOnly}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {prop.enum.map((opt, i) => {
            const v = String(opt);
            return <MenuItem key={v} value={v}>{labels?.[i] ?? v}</MenuItem>;
          })}
        </TextField>
        {help}
      </Box>
    );
  }

  if (type === 'boolean') {
    return (
      <Box>
        <FormControlLabel
          control={<Switch size="small" checked={value === true} disabled={readOnly}
            onChange={(e) => onChange(e.target.checked)} />}
          label={<span>{label}{required ? ' *' : ''}</span>}
        />
        {help}
      </Box>
    );
  }

  if (type === 'number' || type === 'integer') {
    return (
      <Box>
        <FieldHeader label={label} required={required} prop={prop} />
        <TextField
          type="number" size="small" fullWidth disabled={readOnly}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
        {help}
      </Box>
    );
  }

  // string — date/time formats get a typed input; everything else composes via
  // TagValueInput (plain / multiline / template / HTML).
  const format = prop.format;
  const isDateish =
    !flags.html && !flags.template && !flags.multiline &&
    (format === 'time' || format === 'date' || format === 'date-time');

  return (
    <Box>
      <FieldHeader label={label} required={required} prop={prop} />
      {isDateish ? (
        <TextField
          size="small" fullWidth disabled={readOnly}
          type={format === 'time' ? 'time' : format === 'date' ? 'date' : 'datetime-local'}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        />
      ) : (
        <TagValueInput
          value={value == null ? '' : String(value)}
          readOnly={readOnly}
          flags={flags}
          onChange={(v) => onChange(v === '' ? undefined : v)}
        />
      )}
      {help}
    </Box>
  );
}
